/**
 * Cross-side compliance constants — imported by both `src/` and `functions/`.
 *
 * Pinned by contract tests in both projects:
 *   • Frontend: src/shared/compliance/enrollmentReadiness.test.ts asserts
 *     the value of MIN_NUMBER_REFERENCES; src/shared/constants/constants.ts
 *     re-exports for back-compat.
 *   • Backend: functions/api/users/trackComplianceWizardEvent.ts pins
 *     WIZARD_DOC_REQUIREMENT_IDS / NON_DOC_REQUIREMENT_IDS via Zod enums;
 *     functions/utils/getMissingComplianceItems.test.ts exercises both.
 *
 * Lives in shared-utils/ rather than either project's tree because both
 * sides need it and importing across project tsconfig boundaries doesn't
 * work cleanly (separate TS projects).
 */
export const MIN_NUMBER_REFERENCES = 3;

/**
 * Wizard step IDs that aren't backed by an uploaded document. Used in
 * CIO event-tracking and the backend enrollment gap-check.
 */
export const NON_DOC_REQUIREMENT_IDS = [
  "target_date",
  "schedule_meetings",
  "background_check",
  "references",
  "enrollment_documents",
  "theory_training",
  "your_story",
] as const;

/**
 * Doc requirement IDs the wizard collects. Order is stable so downstream
 * formatting (logs, SMS lists, tests) can rely on it.
 */
export const WIZARD_DOC_REQUIREMENT_IDS = [
  "driver_license",
  "ssn",
  "proof_of_income",
  "utility_bill",
  "birth_certificate",
  "medical_exam",
  "updated_resume",
] as const;

export const ALL_WIZARD_REQUIREMENT_IDS = [
  ...NON_DOC_REQUIREMENT_IDS,
  ...WIZARD_DOC_REQUIREMENT_IDS,
] as const;

export type NonDocRequirementId = typeof NON_DOC_REQUIREMENT_IDS[number];
export type WizardDocRequirementId = typeof WIZARD_DOC_REQUIREMENT_IDS[number];
export type WizardRequirementId = NonDocRequirementId | WizardDocRequirementId;

/**
 * Document name aliases that satisfy the `proof_of_income` requirement.
 * New uploads use `proof_of_income`; legacy uploads may still be stored
 * under `pay_stub` or `food_stamps`. Without this fallback, gap checks
 * would false-positive on any student whose income doc predates the rename.
 *
 * Canonical source: src/shared/compliance/incomeDocuments.ts INCOME_DOC_KEYS.
 */
export const INCOME_DOC_NAMES: readonly string[] = [
  "proof_of_income",
  "pay_stub",
  "food_stamps",
];

/**
 * Detail wizard event names — pinned by contract tests on both sides.
 * These fire to CIO every time (no Firestore dedup) and accept arbitrary
 * event properties. The completion-flag payload from
 * `buildWizardCompletionFlags` is merged into every emission so CIO
 * segments can branch on overall wizard progress alongside the
 * detail-specific signal.
 */
export const DETAIL_WIZARD_EVENT_NAMES = [
  "Compliance Wizard - Document Uploaded",
  "Compliance Wizard - Background Check Form Opened",
  "Compliance Wizard - Background Check Checkr Opened",
  "Compliance Wizard - Background Check Form Submitted",
  "Compliance Wizard - Enrollment Document Signed",
  "Compliance Wizard - Enrollment Documents Requested",
  "Compliance Wizard - Target Start Date Selected",
  "Compliance Wizard - Story Submitted",
  "Compliance Wizard - Story Skipped",
  "Compliance Wizard - Resume Builder Started",
  "Compliance Wizard - Resume Builder Skipped",
  "Compliance Wizard - Dashboard Prompt Viewed",
  "Compliance Wizard - Dashboard Prompt Clicked",
  "Compliance Wizard - Post Upload Skipped",
] as const;

export type DetailWizardEventName = typeof DETAIL_WIZARD_EVENT_NAMES[number];

/**
 * Builds the flat-boolean completion payload merged into every wizard
 * CIO event. `completed_<requirement>: true` for each requirement the
 * student has finished, plus `completedCount` / `totalCount` for
 * dashboards. CIO segments filter on these directly — flat keys are
 * the most ergonomic shape for the CIO query builder (nested objects
 * and arrays both require Liquid for membership checks).
 *
 * `justCompletedId` lets the caller fold in the in-flight requirement
 * so the current event's own flag reads `true` (rather than `false`
 * for one race-prone moment between the Firestore write and the read).
 */
export function buildWizardCompletionFlags(
  completedRequirementIds: ReadonlyArray<WizardRequirementId>,
  justCompletedId?: WizardRequirementId | null,
  applicableRequirementIds: ReadonlyArray<WizardRequirementId> = ALL_WIZARD_REQUIREMENT_IDS
): Record<string, boolean | number> {
  const completed = new Set<WizardRequirementId>(completedRequirementIds);
  const applicable = new Set<WizardRequirementId>(applicableRequirementIds);
  if (justCompletedId) {
    completed.add(justCompletedId);
  }
  const flags: Record<string, boolean | number> = {};
  for (const id of ALL_WIZARD_REQUIREMENT_IDS) {
    flags[`completed_${id}`] = completed.has(id);
  }
  flags.completedCount = Array.from(applicable).filter((id) =>
    completed.has(id)
  ).length;
  flags.totalCount = applicable.size;
  return flags;
}
